<script setup>
import { computed } from "vue";
import { parseBlocks, parseInline } from "@/utils/markdown";
import { displayRows } from "@/utils/formulas";
import { splitBlanks } from "@/utils/blanks";
import InlineRuns from "./InlineRuns.vue";

const props = defineProps({
  text: { type: String, default: "" },
  // Plain text makes are shown exactly as written
  plain: { type: Boolean, default: false },
  // Blanks can be clicked to fill them in
  fillable: { type: Boolean, default: false },
  // Answers typed so far, shown in place of their blanks
  blankValues: { type: Object, default: () => ({}) },
});
defineEmits(["blank"]);

// Plain text is only split around its blanks
const plainRuns = computed(() =>
  splitBlanks(props.text).map((run) =>
    run.blank === undefined ? run : { text: `[${run.blank}]`, ...run }
  )
);

// Tables show formula results rather than the formulas themselves
const blocks = computed(() =>
  props.plain
    ? []
    : parseBlocks(props.text).map((block) => {
        if (block.type !== "table") return block;
        const [header, ...rows] = displayRows([block.header, ...block.rows]);
        return { ...block, header, rows };
      })
);

const HEADING_CLASSES = [
  "text-2xl font-bold mb-3",
  "text-xl font-bold mt-6 mb-2",
  "text-lg font-bold mt-4 mb-2",
];
const headingClass = (level) =>
  HEADING_CLASSES[level - 1] || "text-base font-bold mt-3 mb-1";
</script>

<template>
  <div class="text-base text-primary [overflow-wrap:anywhere]">
    <p v-if="plain" class="whitespace-pre-line">
      <InlineRuns
        :runs="plainRuns"
        :fillable="fillable"
        :blankValues="blankValues"
        @blank="$emit('blank', $event)"
      />
    </p>
    <template v-else>
      <template v-for="(block, index) in blocks" :key="index">
        <component
          :is="`h${Math.min(block.level + 2, 6)}`"
          v-if="block.type === 'heading'"
          :class="headingClass(block.level)"
        >
          <InlineRuns
            :runs="
              parseInline(block.text).map((run) => ({ ...run, bold: false }))
            "
            :fillable="fillable"
            :blankValues="blankValues"
            @blank="$emit('blank', $event)"
          />
        </component>
        <component
          :is="block.ordered ? 'ol' : 'ul'"
          v-else-if="block.type === 'list'"
          :start="block.ordered ? block.start : undefined"
          class="mb-3 pl-6"
          :class="block.ordered ? 'list-decimal' : 'list-disc'"
        >
          <li v-for="(item, i) in block.items" :key="i" class="mb-1">
            <InlineRuns
              :runs="parseInline(item)"
              :fillable="fillable"
              :blankValues="blankValues"
              @blank="$emit('blank', $event)"
            />
          </li>
        </component>
        <div
          v-else-if="block.type === 'table'"
          class="mb-4 overflow-x-auto [overflow-wrap:normal]"
        >
          <table class="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th
                  v-for="(cell, i) in block.header"
                  :key="i"
                  class="border-b-2 border-gray-200 px-3 py-2 text-left font-bold"
                >
                  <InlineRuns
                    :runs="parseInline(cell)"
                    :fillable="fillable"
                    :blankValues="blankValues"
                    @blank="$emit('blank', $event)"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, r) in block.rows" :key="r">
                <td
                  v-for="(cell, i) in row"
                  :key="i"
                  class="border-b border-gray-200 px-3 py-2 align-top"
                >
                  <InlineRuns
                    :runs="parseInline(cell)"
                    :fillable="fillable"
                    :blankValues="blankValues"
                    @blank="$emit('blank', $event)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <hr v-else-if="block.type === 'rule'" class="my-4 border-gray-200" />
        <p v-else class="mb-3 whitespace-pre-line">
          <InlineRuns
            :runs="parseInline(block.text)"
            :fillable="fillable"
            :blankValues="blankValues"
            @blank="$emit('blank', $event)"
          />
        </p>
      </template>
    </template>
  </div>
</template>
